import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, Tokens } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface ProductRecord {
  id: number;
  ndc_code: string;
  product_name: string;
  generic_name?: string;
  strength?: string;
  dosage_form?: string;
  route?: string;
  manufacturer?: string;
  country_of_origin?: string;
  origin_confidence?: string;
  coo_scoring_score?: number | null;
  coo_scoring_tier?: string | null;
  api_source_country?: string;
  finished_goods_country?: string;
  made_in_america_score?: number;
  baa_eligible?: boolean;
  taa_eligible?: boolean;
  supply_chain_risk?: string;
  shortage_status?: string;
  therapeutic_category?: string;
  marketing_category?: string;
  application_number?: string;
  orange_book_listed?: boolean;
  confidence_score?: number;
  reconciler_coo?: string | null;
  reconciler_tier?: string | null;
  swarm_qrs_score?: number | null;
  swarm_pns_score?: number | null;
  last_updated?: string;
}

// ============================================================
// STEWARD DISPLAY NAMES
// ============================================================

const STEWARD_DISPLAY: Record<string, string> = {
  steward_fda_establishments: 'FDA Establishments',
  steward_openfda_ndc: 'OpenFDA NDC',
  steward_fda_import_refusals: 'FDA Import Refusals',
  steward_epa_tri: 'EPA TRI',
  steward_fda_recalls: 'FDA Recalls',
  steward_dailymed_spl: 'DailyMed SPL',
  steward_package_ocr: 'Package OCR',
  steward_sec_edgar: 'SEC EDGAR',
  steward_drugs_at_fda: 'Drugs@FDA',
  steward_fda_registration: 'FDA Registration',
  steward_who_prequalification: 'WHO Prequalification',
  steward_dmf_pharmacompass: 'DMF PharmaCompass',
  steward_fda_ndc_directory: 'FDA NDC Directory',
  steward_fda_drug_shortages: 'FDA Drug Shortages',
  steward_pricing: 'Pricing (NADAC)',
  steward_rxnorm: 'RxNorm',
  steward_fda_label_api: 'FDA Label API',
  steward_haiku_spl: 'Haiku SPL Analysis',
  steward_fec_donations: 'FEC Donations',
  steward_lobbying: 'Lobbying (Senate LDA)',
  steward_politician_holdings: 'Politician Holdings',
  steward_pac_contributions: 'PAC Contributions',
  steward_legislation: 'Legislation',
  steward_ccp_flags: 'CCP Flags',
  steward_manufacturer_name_geo: 'Manufacturer Geo',
};

// ============================================================
// HELPERS
// ============================================================

function formatDateTime(iso?: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function normalizeCooTier(tier: string): { label: string; bg: string; fg: string } {
  const upper = tier.toUpperCase();
  if (upper === 'HIGH' || upper === 'NEAR_CERTAIN') {
    return { label: 'High', bg: '#22C55E18', fg: '#22C55E' };
  }
  if (upper === 'MODERATE' || upper === 'MODERATE_HIGH') {
    return { label: 'Medium', bg: '#F59E0B18', fg: '#F59E0B' };
  }
  return { label: 'Low', bg: '#EF444418', fg: '#EF4444' };
}

function tierBadge(tier: string | null | undefined, T: Tokens): React.ReactNode {
  if (!tier) return <span style={{ color: T.textLight }}>--</span>;
  const { label, bg, fg } = normalizeCooTier(tier);
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
      background: bg, color: fg, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function scoreCell(score: number | null | undefined, T: Tokens): React.ReactNode {
  if (score == null) return <span style={{ color: T.textLight }}>--</span>;
  const rounded = Math.round(score);
  const color = rounded >= 75 ? '#22C55E' : rounded >= 50 ? '#F59E0B' : '#EF4444';
  return <span style={{ fontWeight: 700, color }}>{rounded}%</span>;
}

function boolBadge(val: boolean | undefined, T: Tokens): React.ReactNode {
  if (val === undefined || val === null) return <span style={{ color: T.textLight }}>--</span>;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
      background: val ? '#22C55E18' : '#EF444418',
      color: val ? '#22C55E' : '#EF4444',
    }}>
      {val ? 'Yes' : 'No'}
    </span>
  );
}

function countDataCompleteness(products: ProductRecord[]): number {
  if (products.length === 0) return 0;
  const fields: (keyof ProductRecord)[] = [
    'product_name', 'generic_name', 'strength', 'dosage_form', 'route',
    'manufacturer', 'country_of_origin', 'coo_scoring_score', 'api_source_country',
    'finished_goods_country', 'supply_chain_risk',
  ];
  let filled = 0;
  let total = 0;
  for (const p of products) {
    for (const f of fields) {
      total++;
      const v = p[f];
      if (v !== null && v !== undefined && v !== '' && v !== 'Unknown') filled++;
    }
  }
  return total > 0 ? Math.round((filled / total) * 100) : 0;
}

// ============================================================
// CSV EXPORT
// ============================================================

function exportCSV(products: ProductRecord[], agentDisplay: string) {
  const headers = [
    'NDC #', 'Product Name', 'Generic Name', 'Strength', 'Dosage Form', 'Route',
    'Manufacturer', 'Country of Origin', 'COO Tier',
    'API Source Country', 'Finished Goods Country',
    'Risk Level', 'Shortage Status', 'Therapeutic Category',
    'Marketing Category', 'Application #', 'Orange Book',
    'Reconciler COO', 'Reconciler Tier', 'QRS Score', 'PNS Score', 'Last Updated',
  ];

  const escapeCSV = (val: string): string => {
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  const rows = products.map(p => [
    p.ndc_code || '',
    p.product_name || '',
    p.generic_name || '',
    p.strength || '',
    p.dosage_form || '',
    p.route || '',
    p.manufacturer || '',
    p.country_of_origin || '',
    p.coo_scoring_tier ? normalizeCooTier(p.coo_scoring_tier).label : '',
    p.api_source_country || '',
    p.finished_goods_country || '',
    p.supply_chain_risk || '',
    p.shortage_status || 'None',
    p.therapeutic_category || '',
    p.marketing_category || '',
    p.application_number || '',
    p.orange_book_listed ? 'Yes' : 'No',
    p.reconciler_coo || '',
    p.reconciler_tier || '',
    p.swarm_qrs_score != null ? String(Math.round(p.swarm_qrs_score)) : '',
    p.swarm_pns_score != null ? String(Math.round(p.swarm_pns_score)) : '',
    formatDateTime(p.last_updated),
  ].map(escapeCSV));

  const csvContent = [headers.map(escapeCSV).join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const timestamp = new Date().toISOString().slice(0, 10);
  link.download = `total-products-${agentDisplay.replace(/\s+/g, '-').toLowerCase()}-${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function StewardTotalProducts() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const agentDisplay = STEWARD_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');

  const fetchProducts = useCallback(async () => {
    if (!agentName) return;
    try {
      setLoading(true);
      const scoredResp = await fetch(
        `${API_BASE}/api/metasupply/swarm/agents/${agentName}/scored-products`,
        { credentials: 'include' },
      );
      if (!scoredResp.ok) { setError('Failed to fetch steward products'); return; }
      const scoredData = await scoredResp.json();
      const scoredProducts = scoredData.products || [];
      if (scoredProducts.length === 0) { setProducts([]); setError(null); return; }
      const ndcs = Array.from(new Set<string>(scoredProducts.map((p: { ndc: string }) => p.ndc).filter(Boolean)));
      if (ndcs.length === 0) { setProducts([]); setError(null); return; }
      const ndcsParam = ndcs.join(',');
      const productsResp = await fetch(
        `${API_BASE}/metasupply/products?ndcs=${encodeURIComponent(ndcsParam)}&limit=${ndcs.length}&offset=0`,
        { credentials: 'include' },
      );
      if (!productsResp.ok) { setError('Failed to fetch product records'); return; }
      const productsData = await productsResp.json();
      setProducts(productsData.products || []);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filteredProducts = products.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.ndc_code?.toLowerCase().includes(term) ||
      p.product_name?.toLowerCase().includes(term) ||
      p.manufacturer?.toLowerCase().includes(term) ||
      p.generic_name?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);
  const dataCompleteness = countDataCompleteness(products);

  const thStyle: React.CSSProperties = {
    padding: '10px 14px', fontSize: 10, fontWeight: 700, color: T.textLight,
    textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
    textAlign: 'left', borderBottom: `2px solid ${T.border}`, background: T.offWhite,
    position: 'sticky' as const, top: 0, zIndex: 1,
  };
  const tdStyle: React.CSSProperties = {
    padding: '10px 14px', fontSize: 13, whiteSpace: 'nowrap', color: T.textDark,
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading total products...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Breadcrumb + Header */}
      <div>
        <div style={{ fontSize: 12, color: T.textLight, marginBottom: 12 }}>
          <span onClick={() => navigate('/swarm')} style={{ cursor: 'pointer', color: T.navyLight, fontWeight: 600 }}>Swarm</span>
          {' \u203A '}
          <span onClick={() => navigate(`/agent-detail/${agentName}`)} style={{ cursor: 'pointer', color: T.navyLight, fontWeight: 600 }}>{agentDisplay}</span>
          {' \u203A Total Products'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.navy, margin: 0, marginBottom: 4 }}>
              Total Products &#8212; {agentDisplay}
            </h1>
            <p style={{ fontSize: 13, color: T.textLight, margin: 0 }}>
              All products tracked by this intelligence agent with their full product record data
            </p>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 20, background: `${T.navy}10`, color: T.navy, whiteSpace: 'nowrap' }}>
            {agentDisplay}
          </span>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
        <div style={{ padding: '20px 24px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadow }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Total Products</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: T.navy }}>{products.length.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Tracked by this agent</div>
        </div>
        <div style={{ padding: '20px 24px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadow }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Data Completeness</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#22C55E' }}>{dataCompleteness}%</div>
          <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Avg across all products</div>
        </div>
        <div style={{ padding: '20px 24px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadow }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Last Updated</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.navy }}>
            {products.length > 0
              ? formatDateTime(products.reduce((latest, p) => (p.last_updated && (!latest || p.last_updated > latest)) ? p.last_updated : latest, '' as string))
              : '--'}
          </div>
          <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Most recent collection run</div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, fontWeight: 600 }}>{error}</div>
      )}

      {/* Search bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 16px', boxShadow: T.shadow }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
        <input type="text" placeholder="Search by NDC, product name, or manufacturer..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: T.textDark, background: 'transparent' }} />
        {searchTerm && <button onClick={() => setSearchTerm('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textLight, fontSize: 12, fontFamily: 'inherit', padding: 0 }}>Clear</button>}
        <span style={{ fontSize: 11, color: T.textLight, whiteSpace: 'nowrap' }}>{filteredProducts.length.toLocaleString()} products</span>
        <button onClick={() => exportCSV(filteredProducts, agentDisplay)} style={{ border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: T.textDark, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Export CSV</button>
      </div>

      {/* Products Table */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadow, overflow: 'hidden' }}>
        {filteredProducts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: T.textLight }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
              {searchTerm ? 'No Matching Products' : 'No Products Yet'}
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              {searchTerm
                ? `No products match "${searchTerm}". Try a different search term.`
                : `The ${agentDisplay} agent has not collected data for any products yet. Products will appear here once the case officer triggers this agent.`}
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '8px 16px', fontSize: 11, color: T.textLight, fontStyle: 'italic', borderBottom: `1px solid ${T.borderLight}` }}>
              Scroll horizontally to see all columns &#8594;
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1800 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>NDC #</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>Product Name</th>
                    <th style={{ ...thStyle, minWidth: 160 }}>Generic Name</th>
                    <th style={thStyle}>Strength</th>
                    <th style={thStyle}>Dosage Form</th>
                    <th style={thStyle}>Route</th>
                    <th style={{ ...thStyle, minWidth: 180 }}>Manufacturer</th>
                    <th style={thStyle}>Country of Origin</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>COO Tier</th>
                    <th style={thStyle}>API Source Country</th>
                    <th style={thStyle}>Finished Goods Country</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Risk Level</th>
                    <th style={thStyle}>Shortage Status</th>
                    <th style={thStyle}>Therapeutic Category</th>
                    <th style={thStyle}>Marketing Category</th>
                    <th style={thStyle}>Application #</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Orange Book</th>
                    <th style={thStyle}>Reconciler COO</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Reconciler Tier</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>QRS Score</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>PNS Score</th>
                    <th style={thStyle}>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.map((p, idx) => {
                    const cooColor =
                      (p.country_of_origin || '').toLowerCase().includes('united states') || (p.country_of_origin || '').toLowerCase() === 'usa'
                        ? '#22C55E'
                        : (p.country_of_origin || '').toLowerCase() === 'china' ? '#EF4444' : T.textDark;
                    return (
                      <tr key={p.id || `${p.ndc_code}-${idx}`}
                        onClick={() => p.id && navigate(`/product/${p.id}`)}
                        style={{ cursor: p.id ? 'pointer' : 'default', borderBottom: `1px solid ${T.borderLight}`, transition: 'background 0.15s' }}
                        onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`}
                        onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}
                      >
                        <td style={{ ...tdStyle, fontWeight: 600, color: T.navy }}>{p.ndc_code || '--'}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.product_name || '--'}</td>
                        <td style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.generic_name || '--'}</td>
                        <td style={tdStyle}>{p.strength || '--'}</td>
                        <td style={tdStyle}>{p.dosage_form || '--'}</td>
                        <td style={tdStyle}>{p.route || '--'}</td>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.manufacturer || '--'}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: cooColor }}>{p.country_of_origin || '--'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{tierBadge(p.coo_scoring_tier, T)}</td>
                        <td style={tdStyle}>{p.api_source_country || '--'}</td>
                        <td style={tdStyle}>{p.finished_goods_country || '--'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {p.supply_chain_risk ? (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                              background: p.supply_chain_risk === 'high' ? '#EF444418' : p.supply_chain_risk === 'medium' ? '#F59E0B18' : '#22C55E18',
                              color: p.supply_chain_risk === 'high' ? '#EF4444' : p.supply_chain_risk === 'medium' ? '#F59E0B' : '#22C55E',
                              textTransform: 'capitalize' }}>{p.supply_chain_risk}</span>
                          ) : <span style={{ color: T.textLight }}>--</span>}
                        </td>
                        <td style={tdStyle}>
                          {p.shortage_status && p.shortage_status !== 'None' ? (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#F59E0B18', color: '#F59E0B' }}>{p.shortage_status}</span>
                          ) : <span style={{ color: T.textLight }}>None</span>}
                        </td>
                        <td style={tdStyle}>{p.therapeutic_category || '--'}</td>
                        <td style={tdStyle}>{p.marketing_category || '--'}</td>
                        <td style={tdStyle}>{p.application_number || '--'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{boolBadge(p.orange_book_listed, T)}</td>
                        <td style={tdStyle}>{p.reconciler_coo || '--'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{tierBadge(p.reconciler_tier, T)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{scoreCell(p.swarm_qrs_score, T)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{scoreCell(p.swarm_pns_score, T)}</td>
                        <td style={{ ...tdStyle, color: T.textLight, fontSize: 12 }}>{formatDateTime(p.last_updated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
          <span style={{ fontSize: 12, color: T.textLight }}>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredProducts.length)} of {filteredProducts.length.toLocaleString()}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              style={{ border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: currentPage === 1 ? 'default' : 'pointer', color: currentPage === 1 ? T.textLight : T.textDark, fontFamily: 'inherit', opacity: currentPage === 1 ? 0.5 : 1 }}>Prev</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) { pageNum = i + 1; }
              else if (currentPage <= 3) { pageNum = i + 1; }
              else if (currentPage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
              else { pageNum = currentPage - 2 + i; }
              return (
                <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                  style={{ border: `1px solid ${pageNum === currentPage ? T.navy : T.border}`, background: pageNum === currentPage ? T.navy : T.white, color: pageNum === currentPage ? T.white : T.textDark, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontWeight: pageNum === currentPage ? 700 : 400, fontFamily: 'inherit' }}>{pageNum}</button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              style={{ border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: currentPage === totalPages ? 'default' : 'pointer', color: currentPage === totalPages ? T.textLight : T.textDark, fontFamily: 'inherit', opacity: currentPage === totalPages ? 0.5 : 1 }}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
